<script setup lang="ts">
import { computed } from 'vue';
import RexStatisticV3 from '@/rex-components/RexStatisticV3.vue';
import RexTagV3 from '@/rex-components/RexTagV3.vue';
import {
  graphTooltipInfoConfig,
  anomalyInfoObject,
  anomalyTypeColorMapper,
  severityTypeMapper,
  severityKeyMapper,
  anomalyTagTextColor,
} from '@/components/charts/utils/utils.config';
import { GraphTooltipKeysType } from '@/components/charts/utils/utils.types';
import {
  AnomalyDataObjectType,
  GraphDataNamesEnum,
} from '@/components/Common/graph.types';
import { formatDate } from '@/utils/time/formatDate';
import { useI18n } from 'vue-i18n';
import { RexConfigProvider } from '@/rex-components/rex.comp.config';

interface Props {
  tooltipInfoData: GraphTooltipKeysType;
  anomalyInfo?: AnomalyDataObjectType;
  isAnomalySelected?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  anomalyInfo: () => anomalyInfoObject,
  isAnomalySelected: false,
});
const { t } = useI18n();

function getFormattedData(key) {
  return props.tooltipInfoData[key] ?? 'NA';
}

const getTagText = computed(
  () => severityTypeMapper[props.anomalyInfo[GraphDataNamesEnum.SEVERITY]],
);
const getAnomalyKey = computed(
  () => severityKeyMapper[props.anomalyInfo[GraphDataNamesEnum.SEVERITY]],
);
const getTagColor = computed(() => anomalyTypeColorMapper[getAnomalyKey.value]);
const getTagTextColor = computed(
  () => anomalyTagTextColor[getAnomalyKey.value],
);

const roundValues = (val) => Math.round(val * 1000) / 1000;
const getSelectedAnomalyFooterInfo = computed(() => {
  const upperThreshold = Number(
    props.anomalyInfo[GraphDataNamesEnum.UPPER_THRESHOLD],
  );
  const lowerThreshold = Number(
    props.anomalyInfo[GraphDataNamesEnum.LOWER_THRESHOLD],
  );
  const groundTruth = props.anomalyInfo.y || 0;

  return upperThreshold < groundTruth
    ? t('metricIncreasedBreachingUpperThreshold', [
        roundValues(groundTruth),
        roundValues(upperThreshold),
      ])
    : t('metricDecreasedBreachingLowerThreshold', [
        roundValues(groundTruth),
        roundValues(lowerThreshold),
      ]);
});
</script>

<template>
  <div>
    <div
      v-if="props.tooltipInfoData.time"
      class="anmly-graph-tooltip-info-date"
    >
      {{
        formatDate(
          new Date(props.tooltipInfoData.time).getTime(),
          0,
          'YYYY-MM-DD HH:mm A',
        )
      }}
    </div>
    <div class="anomaly-graph-tooltip-info">
      <RexStatisticV3
        v-for="(obj, i) in graphTooltipInfoConfig"
        :value="getFormattedData(obj.key)"
        :title="obj.label"
        :key="`${obj.key}-${i}`"
        class="anmly-graph-footer-statistic"
      />
    </div>
    <div v-if="isAnomalySelected" class="anmly-selected-info-box">
      <div class="anmly-selected-info-title">
        <RexConfigProvider
          :theme="{
            components: {
              Tag: {
                colorTextLightSolid: getTagTextColor,
              },
            },
          }"
        >
          <RexTagV3 :color="getTagColor" class="anmly-selected-info-tag-text">
            {{ getTagText }}
          </RexTagV3>
        </RexConfigProvider>
        <div class="anmly-selected-info-sub-text">
          {{
            formatDate(
              new Date(anomalyInfo.x).getTime(),
              0,
              'YYYY-MM-DD HH:mm A',
            )
          }}
        </div>
      </div>
      <div class="anmly-selected-info-text">
        {{ getSelectedAnomalyFooterInfo }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.anmly-graph-footer-statistic {
  border: 0.063rem solid;
  width: 100%;
  margin: 0rem 1rem;
  padding-left: 1rem;
  background-color: $bg-primary;
  border-radius: 0.5rem;
}
.anomaly-graph-tooltip-info {
  @include flex-center-vert;
  margin-top: 1rem;
}
.anmly-selected-info-box {
  padding: 1rem 1rem 0rem 1rem;
  margin-top: 1rem;
  border-top: 0.01rem solid $border-gray;
}
.anmly-selected-info-title {
  @include flex-center-vert;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.anmly-selected-info-tag-text {
  font-weight: 700;
}
.anmly-selected-info-text {
  color: $table-header-text-grey;
  font-size: 1.125rem;
}
.anmly-selected-info-sub-text {
  font-size: 0.875rem;
}
.anmly-graph-tooltip-info-date {
  margin-left: 1.2rem;
  margin-top: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
}
</style>
